import {Injectable, OnDestroy} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {RuleSet} from '../_typing/query-builder';
import {ApiService} from "./api/api.service";
import {forkJoin, Observable, of, Subject} from "rxjs";
import {AppScope} from "./app_scope.service";
import {catchError, concatMap, last, map, tap} from "rxjs/operators";
import {DateTimePeriodService} from "./date-time-period.service";
import {Event as WireEvent} from "../_models/event";
import {deepCopy} from "../lib/utils";
import {EventType} from '../_models/event-type';
import {SearchQueryOptions} from "./api/search-query-options";
import {EventComponent} from "../_models/event-component";
import {ComponentType} from "../_models/component-type";
import {QueryBuilderService} from './query_builder.service';
import {ModelChangesService} from "./api/model-changes.service";
import {ConstantPropertyDataService, PropByRelationshipResponse} from "../data/constant-property-data.service";
import {ListResponse} from "./api/response-types";
import {ConstantProperty} from "../_models/constant-property";
import {ComponentTypeByRelationshipResponse, ComponentTypeDataService} from "../data/component-type-data.service";
import {ComponentDataService} from '../data/component-data.service';
import {EventDataService} from "../data/event-data.service";
import {CustomEventsDataService} from '../tables/custom-events-table/custom-events-data.service';
import {KeyMap, ModelAttribute, ModelID, IDMap} from '../_typing/generic-types';
import {
    getRelationWithIdFilter,
    getRelationWithManyIdsFilter
} from "./api/filter_utils";
import {ConstantComponent} from '../_models/constant-component';
import {GenericConstantApiResponse, GenericField, isConstant, ConstantField} from "../_models/api/generic-constant";
import {ConstantLockedDict} from './lock-data.service';
import {IDateTimePeriod} from "../_typing/date-time-period";
import {NotificationService} from "./notification.service";
import {moment} from './timezone-selector.service';
import {WireComponent} from "../_models/component";
import {Comment} from "../_models/comment";
import {EventTypeComponentTypeMap, EventTypeComponentTypeUniqueMap} from "../_models/event-type-component-type-map";
import {getComponentTypeIds} from "../tables/custom-events-table/utils";
import {ConfigTypeDateFilter} from "../_typing/config/config-type-date-filter";
import {UserService} from "./user.service";
import {ConfigStub} from "../_typing/config-stub";

@Injectable({
    providedIn: 'root'
})
export class CustomEventsService implements OnDestroy {
    schema = {
        id: null,
        type: 'event', // ***
        attributes: {
            base_type: 'event',
            comment: null,
            start: null,
            end: null,
            created_on: null,
            changed_on: null,
            event_type_name: null,
            custom_constants: {},
        },
        relationships: {
            components: {data: []},
            series_list: {data: []},
            created_by: {data: {id: null, type: 'users'}},
            changed_by: {data: {id: null, type: 'users'}}
        }
    };
    event_state: { [key: string]: { state: string, message: string } };
    error_list: any;

    private readonly onDestroy = new Subject<void>();

    modelChanges: ModelChangesService;

    constructor(private appScope: AppScope,
                private http: HttpClient,
                private api: ApiService,
                private dateTimePeriodService: DateTimePeriodService,
                private queryBuilderService: QueryBuilderService,
                private propertyDataService: ConstantPropertyDataService,
                private componentTypeData: ComponentTypeDataService,
                private componentData: ComponentDataService,
                private eventDataService: EventDataService,
                private customEventsDataService: CustomEventsDataService,
                private notification: NotificationService,
                private userService: UserService
    ) {
    }

    getMapConstantsToEvents(event_response: ListResponse<WireEvent>, constant_properties_ids: string[]): Observable<ListResponse<WireEvent>> {

        let events = event_response.data;
        const $constants = this.getEventConstants(events.map(e => e.id), constant_properties_ids);
        const $constant_properties = this.propertyDataService.getConstantProperties(constant_properties_ids);

        return forkJoin([$constants, $constant_properties]).pipe(
            map(([constants, constant_properties]) => {
                let cp_id_dict = constant_properties.data.reduce((dict, cp) => {
                    dict[cp.id] = cp;
                    return dict;
                }, {});
                this.mapApiEventConstants(event_response.data, constants, cp_id_dict);
                return event_response;
            })
        );

    }

    checkStartAndEndDates(toSave: WireEvent | WireComponent | Partial<Comment>, startAtt: 'start' | 'start_time', endAtt: 'end' | 'end_time', changeIds?: string[]): boolean {
        if (toSave.id !== null && (changeIds && !changeIds.includes(toSave.id))) return true;

        if (toSave.attributes[startAtt] && toSave.attributes[endAtt] && moment(toSave.attributes[endAtt]).isBefore(moment(toSave.attributes[startAtt]))) {
            this.notification.openError(`End date must be later than start date`);
            return false;
        }
        return true;
    }

    getEventConstants(event_ids: ModelID[], constant_properties_ids: ModelID[], attributes?: ModelAttribute[]) {
        return this.customEventsDataService.getApiEventConstants(event_ids, constant_properties_ids, attributes);
    }

    mapApiEventConstants(events, eventConstants: GenericConstantApiResponse, cp_dict: KeyMap<ConstantProperty>): void {
        events.forEach(event => {
            let constants: { [key: string]: any } = eventConstants?.data[event.id] || {};
            event.attributes.custom_constants = Object.keys(constants).reduce((prevValue, key) => {
                if (cp_dict[key]) {
                    if (isConstant(constants[key])) {
                        prevValue[cp_dict[key].attributes.name] = constants[key].value;
                    } else {
                        prevValue[cp_dict[key].attributes.name] = null;
                    }
                }
                return prevValue;
            }, {});
        });
    }

    mapEventConstants(events, eventConstants, cp_dict): void {
        events.forEach(event => {
            let constants = eventConstants['data'][event.id] || {};
            event.attributes.custom_constants = Object.keys(constants).reduce((prevValue, value) => {
                prevValue[cp_dict[value].attributes.name] = constants[value];
                return prevValue;
            }, {});
        });
    }

    updateLockedProperties(locked_dict: ConstantLockedDict, events: WireEvent[], properties: ConstantProperty[], constant_data: GenericConstantApiResponse, attributes?: string[]): ConstantLockedDict {
        const constants = constant_data?.data || [];
        events.forEach(e => {
            locked_dict[e.id] = attributes.reduce((locked, att) => {
                return Object.assign(locked, {[att]: constants[e.id]?.[att]?.locked})
            }, {})
            properties.forEach(p => {
                let ec = constants[e.id]?.[p.id];
                locked_dict[e.id][p.id] = ec ? ec.locked : false;
            })
        })
        return locked_dict;
    }

    updateEventComponentLinksLocked(locked_dict: ConstantLockedDict, event_components: IDMap<EventComponent>) {
        Object.values(event_components).forEach(ec => {
            locked_dict[ec.relationships.event.data.id] = Object.assign(locked_dict[ec.relationships.event.data.id] || {},
                {component: ec.attributes.locked});
        })
        return locked_dict;
    }

    getConstantPropertiesByEventTypeIds(event_type_ids: string[], cp_ids?: string[], names?: string[]): Observable<ListResponse<ConstantProperty>> {
        return this.propertyDataService.getConstantPropertiesByRelationshipIds(event_type_ids, 'event_types', cp_ids, names);

    }

    getStreamedConstantPropertiesByEventTypeIds(event_type_ids: string[], cp_ids?: string[], column_names?: string[]): Observable<PropByRelationshipResponse> {
        return this.propertyDataService.streamPropertiesByRelationshipIds(event_type_ids, 'event_types', cp_ids, column_names)
            .pipe(last());
    }

    getComponentsByEvent(event_ids: string[], component_type_ids?: string[]): Observable<any> {
        let filters;
        if (component_type_ids) {
            filters = [
                getRelationWithManyIdsFilter('component_type', component_type_ids)
            ];
        }

        return this.componentData.streamComponentsByParentIds(event_ids, 'events', null, null, filters)
            .pipe(last());
    }

    getComponentTypeProperties(component_type_ids: string[], prop_ids?: ModelID[]): Observable<ListResponse<ConstantProperty>> {
        return this.propertyDataService.getConstantPropertiesByRelationshipIds(component_type_ids, 'component_types', prop_ids);
    }

    getEventTypeComponentTypes(event_type_ids: string[], ctIds?: ModelID[]): Observable<any> {
        return this.componentTypeData.getComponentTypesByRelationshipIds(event_type_ids, 'event_types', ctIds);
    }

    getComponentTypesByEventTypes(event_type_ids: string[], columns?: string[], ids?: string[]): Observable<any> {
        return this.componentTypeData.streamComponentTypesByRelationshipIds(event_type_ids, 'event_types', ids, columns)
            .pipe(last());
    }

    getEventTypePropertiesForComponentTypes(component_type_ids: string[]): Observable<ListResponse<ConstantProperty>> {
        return this.propertyDataService.getChildPropertiesByParentIds(component_type_ids, 'component_types', 'event_types');
    }

    getComponentTypeEventTypes(component_type_ids: string[], etIds?: ModelID[]): Observable<ListResponse<EventType>> {
        return this.eventDataService.getEventTypesByRelationshipIds(component_type_ids, 'component_types', etIds);
    }

    getEventComponents(event_ids, component_ids): Observable<any> {
        return this.customEventsDataService.getEventComponents(event_ids, component_ids);
    }

    getEventTypeComponentTypeRelationships(eventTypeIds?: ModelID[], componentTypeIds?: ModelID[]): Observable<ListResponse<EventTypeComponentTypeMap>> {
        if (!eventTypeIds?.length && !componentTypeIds?.length) return of(null);
        const options = new SearchQueryOptions();

        options.filters = [];
        if (eventTypeIds?.length) {
            options.filters.push(getRelationWithManyIdsFilter('event_type', eventTypeIds));
        }

        if (componentTypeIds?.length) {
            options.filters.push(getRelationWithManyIdsFilter('component_type', componentTypeIds));
        }
        return this.api.event_type_component_type_map.searchMany(options);
    }

    getEventTypeComponentTypeQueryMap(eventTypeComponentTypes: EventTypeComponentTypeMap[], dateFilterConfig?: Partial<ConfigTypeDateFilter>, dtp?: IDateTimePeriod): KeyMap<KeyMap<any>> {
        const singleQuery = (componentTypeId: ModelID, eventTypeId: ModelID, dateFilters: any) => {
            const query = {
                and: [getRelationWithIdFilter('component_type', componentTypeId),
                    {
                        not: {
                            and: [
                                {
                                    name: 'event_components', op: 'any', val:
                                        {
                                            name: 'component',
                                            op: 'has',
                                            val: getRelationWithIdFilter('component_type', componentTypeId)
                                        }
                                }, {
                                    name: 'event_components', op: 'any', val:
                                        {
                                            name: 'event',
                                            op: 'has',
                                            val: getRelationWithIdFilter('event_type', eventTypeId)
                                        }
                                }
                            ]
                        }

                    }]
            };
            if (dateFilters) {
                query.and.push(dateFilters);
            }
            return [query];
        };


        let etCtQueryDict = {};
        eventTypeComponentTypes.forEach(item => {
            const etId = item.relationships.event_type.data.id;
            const ctId = item.relationships.component_type.data.id;
            if (!etCtQueryDict[etId]) {
                etCtQueryDict[etId] = {};
            }
            let dateFilters: any;
            if (dateFilterConfig?.constant_property_time?.[ctId] || dateFilterConfig?.attribute_time?.[ctId]) {
                dateFilters = this.componentData.generateDateFilters(dateFilterConfig, ctId, dtp);
            }
            const safeDateFilters = dateFilters ? [dateFilters] : [];
            etCtQueryDict[etId][ctId] = item.attributes.has_one_event ? singleQuery(ctId, etId, dateFilters) : [getRelationWithIdFilter('component_type', ctId), ...safeDateFilters];
        });
        return etCtQueryDict;
    }

    getComponentTypeComponentTypes(component_type_id: string): Observable<ListResponse<ComponentType>> {
        const options = new SearchQueryOptions();
        options.filters = this.componentTypeData.getComponentRelationshipFilters(component_type_id);
        return this.api.component_type.searchMany(options);
    }

    getEventTypeComponentTypeData(eventTypeIds: ModelID[], componentTypeIds: ModelID[], dateFilterConfig?: Partial<ConfigTypeDateFilter>, dtp?: IDateTimePeriod): Observable<{
        component_types: ComponentType[];
        component_types_dict: Record<string, ComponentType>;
        component_columns: string[];
        etCtDict: IDMap<IDMap<EventTypeComponentTypeUniqueMap>>
        etCtQueryDict: KeyMap<KeyMap<any>>;
        componentTypeResponse: ComponentTypeByRelationshipResponse;
        eventTypeComponentTypeData: EventTypeComponentTypeMap[]
    }> {

        return forkJoin([this.getComponentTypesByEventTypes(eventTypeIds, null, componentTypeIds),
            this.getEventTypeComponentTypeRelationships(eventTypeIds, componentTypeIds)])
            .pipe(
                map(([componentTypeResponse, eventTypeComponentTypes]) => {
                    const component_types = componentTypeResponse.component_types;
                    const component_types_dict = componentTypeResponse.ct_dict;
                    const component_columns = componentTypeIds;
                    const etCtDict: IDMap<IDMap<EventTypeComponentTypeUniqueMap>> = {};
                    const eventTypeComponentTypeData: EventTypeComponentTypeMap[] = eventTypeComponentTypes.data;
                    eventTypeIds.forEach(etId => {
                        etCtDict[etId] = {};
                        componentTypeResponse.ct_list_dict[etId]?.forEach(ctId => {
                            const relationship = eventTypeComponentTypes.data.find(etCt => etCt.relationships.component_type.data.id === ctId && etCt.relationships.event_type.data.id === etId);
                            etCtDict[etId][ctId] = {
                                hasOneComponent: relationship.attributes.has_one_component,
                                hasUniqueList: relationship.attributes.has_one_event,
                                name: component_types.find(ct => ct.id === ctId)?.attributes.name
                            }
                        })
                    })
                    const etCtQueryDict = this.getEventTypeComponentTypeQueryMap(eventTypeComponentTypes.data, dateFilterConfig, dtp);
                    return {
                        component_types,
                        component_types_dict,
                        component_columns,
                        etCtDict,
                        etCtQueryDict,
                        componentTypeResponse,
                        eventTypeComponentTypeData
                    };
                })
            );
    }

    generateQueryBuilderFilter(typedIds: ModelID[], query: KeyMap<RuleSet | null>, relation: 'event_type' | 'component_type' = 'event_type', op: 'and' | 'or' = 'and') {
        let filter: { [key: string]: any[] } = {[op]: []};
        if (query) {
            let typesWithQuery = typedIds.filter(typeId => query[typeId]);
            // construct filter for event_types with query
            for (let typeId of typesWithQuery) {
                let typeQuery: any = [
                    getRelationWithIdFilter(relation, typeId),
                    this.queryBuilderService.toJsonQuery(query[typeId], relation === 'event_type' ? 'EVENT' : 'COMPONENT')
                ];
                filter[op].push({"and": typeQuery});
            }

            // include all event_types that don't have any query
            let typeWithoutQuery: string[] = typedIds.filter(eventTypeId => !query[eventTypeId]);
            if (typeWithoutQuery.length) {
                filter[op].push(getRelationWithManyIdsFilter(relation, typeWithoutQuery));
            }
        }
        return [filter];
    }

    calculateDuration(row) {
        if (row.attributes.start && row.attributes.end) {
            let rHours, rMin, rSec;
            if (new Date(row.attributes.start).getTime() < new Date(row.attributes.end).getTime()) {
                // @ts-ignore
                const dur_ms = (new Date(row.attributes.end)) - (new Date(row.attributes.start));

                const hours = Math.floor(dur_ms / 3600000);
                const minutes = Math.floor(dur_ms % 3600000 / 60000);
                const seconds = dur_ms / 1000 - hours * 3600 - minutes * 60;
                rHours = (hours >= 10) ? hours : "0" + hours;
                rMin = (minutes >= 10) ? minutes : "0" + minutes;
                rSec = (seconds >= 10) ? seconds : "0" + seconds;
            } else {
                rHours = '00';
                rMin = '00';
                rSec = '00';
            }

            return rHours + ':' + rMin + ':' + rSec;
        }
    }

    saveEvent(event_to_save, event): Observable<any> {
        const ctrl = this;
        ctrl.event_state[event.id] = {state: 'dirty', message: 'Not yet saved'};

        return this.api.event.obsSave(event_to_save).pipe(map(result => {
            result.temp_id = deepCopy(event.id);
            event.id = result.data.id;
            ctrl.event_state[event.id] = {state: 'success', message: 'Event saved successfully'};
            return result;
        }), catchError((err: any, caught: any) => {
            console.log('ERROR: CustomEventsService (saveEvent)', err, caught);
            ctrl.event_state[event.id] = {state: 'error', message: err.statusText};
            ctrl.error_list.push({event: event.id, error: err});
            return of({event: event.id, error: err});
        }));
    }

    patchEvent(event: Partial<WireEvent>, event_component_changes = null): Observable<any> {
        const ctrl = this;
        ctrl.event_state[event.id] = {state: 'dirty', message: 'Not yet saved'};

        delete event.attributes.properties_data_map;
        //let $patch = this.modelChanges.obsPatch({api: 'event'}, event);
        let $patch = this.api.event.obsPatch(event);
        let flat_event_components = [];
        if (event_component_changes) {
            Object.values(event_component_changes).map(item => flat_event_components = flat_event_components.concat(item));
        }

        return $patch.pipe(concatMap(result => {
            ctrl.event_state[event.id] = {state: 'success', message: 'Event saved successfully'};
            if (flat_event_components.length > 0) {
                return forkJoin(flat_event_components).pipe(map(() => result));
            } else {
                return of(result);
            }
        }), catchError((err: any, caught: any) => {
            console.log('ERROR: CustomEventsService (saveEvent), event not saved', err, caught);
            ctrl.event_state[event.id] = {state: 'error', message: err.statusText};
            ctrl.error_list.push({event: event.id, error: err});
            return of({event: event.id, error: err});
        }));
    }

    deleteEvent(event_id, index): Observable<any> {
        const ctrl = this;
        return this.api.event.obsDelete(event_id)
            .pipe(tap(result => result),
                catchError((err: any, caught: any) => {
                    console.log('ERROR: CustomEventsService (deleteEvent)', err, caught);
                    ctrl.event_state[event_id] = {state: 'error', message: err.statusText};
                    ctrl.error_list.push({event: event_id, error: err});
                    return of({event: event_id, index: index, error: err});
                })
            );
    }

    patchConstant(constant_component: ConstantComponent, parent, prop, data_type: string): Observable<any> {
        const ctrl = this;
        ctrl.event_state[parent.id + prop] = {state: 'dirty', message: 'Not yet saved'};
        return this.api.constant_component.obsPatch(constant_component)
            .pipe(tap(result => {
                ctrl.event_state[parent.id + prop] = {state: 'success', message: 'Property saved successfully'};
            }), catchError((err: any, caught: any) => {
                console.log('ERROR: CustomEventsService (patchConstant)', err, caught);
                ctrl.event_state[parent.id + prop] = {state: 'error', message: 'Not saved. ' + err.statusText};
                return of({component: parent.id + prop, error: err});
            }));
    }

    saveConstant(constant_component, parent, prop, data_type: string): Observable<any> {
        const ctrl = this;
        ctrl.event_state[parent.id + prop] = {state: 'dirty', message: 'Not yet saved'};
        return this.api.constant_component.obsSave(constant_component)
            .pipe(tap(result => {
                ctrl.event_state[parent.id + prop] = {state: 'success', message: 'Property saved successfully'};
            }), catchError((err: any, caught: any) => {
                console.log('ERROR: CustomEventsService (saveConstant)', err, caught);
                ctrl.event_state[parent.id + prop] = {state: 'error', message: 'Not saved. ' + err.statusText};
                return of({component: parent.id + prop, error: err});
            }));
    }

    checkForAutoLinkingRules(eventTypeIds: ModelID[]): Observable<boolean> {
        if (eventTypeIds?.length && this.userService.canRunAutoLinking()) {
            const checks = eventTypeIds.map(
                etId => this.api.getComponentTypeLinks({event_type_id: etId})
            );
            return forkJoin(checks).pipe(
                map(results => {
                    return results.some(
                        response => (response?.links || []).some(l => !!l.automatic_linking_rule)
                    );
                }))
        } else {
            return of(false)
        }
    }

    getEventTypeIdsSet(eventTypes: ConfigStub<EventType>[]): ModelID[] {
        return [
            ...new Set(
                (eventTypes || [])
                    .map(et => String(et?.id || '').trim())
                    .filter(Boolean)
            )
        ];
    }

    runAutomaticLinkingForConfiguredEventTypes(eventTypeIds: ModelID[]): Observable<any> {
        if (!eventTypeIds?.length) {
            return of(null);
        }
        return forkJoin(
            eventTypeIds.map(etId =>
                this.api.runAutomaticComponentLinks({event_type_id: etId}).pipe(
                    map(() => ({success: true, eventTypeId: etId})),
                    catchError(() => of({success: false, eventTypeId: etId}))
                )
            )
        ).pipe(tap((results) => {
                const failures = results.filter(r => !r.success);
                if (failures.length === 0) {
                    this.notification.openSuccess(
                        "Queued automatic linking run for configured event type rules."
                    );
                } else if (failures.length === results.length) {
                    this.notification.openError(
                        "Failed to queue automatic linking run for all event types."
                    );
                } else {
                    this.notification.openError(
                        `Queued ${results.length - failures.length} of ${results.length} event types. ` +
                        `${failures.length} failed to queue.`
                    );
                }
            })
        );
    }

    // TODO put these somewhere generic, maybe appScope or utils?
    compareById(option, value): boolean {
        if (value) {
            return option.id === value.id;
        }
        return false;
    }

    ngOnDestroy(): void {
        this.onDestroy.next();
        this.onDestroy.unsubscribe();
    }
}
